import axios from "axios";

/**
 * Fetches data from MongoDB via the proxy API
 * @param {string} collectionName - The name of the MongoDB collection
 * @param {string} _id - The document ID to retrieve
 * @returns {Promise} - Returns the data from the API
 */
const getDataFromMongoDb = async (collectionName, _id) => {
    try {
        const baseUrl = "https://server.causalfunnels.com/api/get-dashboard-data";
        
        const response = await axios.get(baseUrl, {
            params: {
                collectionName: collectionName,
                _id: _id
            },
            headers: {
                'Content-Type': 'application/json'
            }
        });
        
        return response.data.data;
    } catch (error) {
        console.error("Error fetching data from MongoDB:", error);
        throw error;
    }
};

/**
 * Fetches data from a specific path within a MongoDB document via the proxy API
 * @param {string} collectionName - The name of the MongoDB collection
 * @param {string} _id - The document ID to retrieve data from
 * @param {string} path - The nested path to retrieve (e.g., "data.2025.09.29")
 * @returns {Promise} - Returns the data from the specified path
 */
const getDataInMongoDbByPath = async (collectionName, _id, path) => {
    try {
        const baseUrl = "https://server.causalfunnels.com/api/get-dashboard-data-by-path";

        const response = await axios.get(baseUrl, {
            params: {
                collectionName: collectionName,
                _id: _id,
                path: path
            },
            headers: {
                'Content-Type': 'application/json'
            }
        });

        return response.data;
    } catch (error) {
        console.error("Error fetching data from MongoDB by path:", error);
        throw error;
    }
};

/**
 * Fetches data from a specific path within a MongoDB document via the proxy API
 * @param {string} collectionName - The name of the MongoDB collection
 * @param {string} _id - The document ID to retrieve data from
 * @param {string} path - The nested path to retrieve (e.g., "data.2025.09.29")
 * @returns {Promise} - Returns the data from the specified path
 */
const getKeysInMongoDbByPath = async (collectionName, _id, path) => {
    try {
        const baseUrl = "https://server.causalfunnels.com/api/get-graph-keys-by-path";

        const response = await axios.get(baseUrl, {
            params: {
                collectionName: collectionName,
                _id: _id,
                path: path
            },
            headers: {
                'Content-Type': 'application/json'
            }
        });

        return response.data;
    } catch (error) {
        console.error("Error fetching data from MongoDB by path:", error);
        throw error;
    }
};

/**
 * Updates data in MongoDB via the proxy API
 * @param {string} collectionName - The name of the MongoDB collection
 * @param {string} graphName - The graph name identifier
 * @param {Array<string>} path - Array representing the nested path to update (e.g., ["airkauaihelicopters", "chatbot_data", "20"])
 * @param {string|Array|Object|any} value - The value to update (can be string, array, object, or any type)
 * @returns {Promise} - Returns the response from the API
 */
const updateDataInMongoDb = async (collectionName, graphName, path, value) => {
    try {
        const baseUrl = `https://server.causalfunnels.com/api/update-dashboard-data`;
        
        const response = await axios.put(baseUrl, {
            collectionName: collectionName,
            graphName: graphName,
            path: path,
            value: value
        }, {
            headers: {
                'Content-Type': 'application/json'
            }
        });
        
        return response.data;
    } catch (error) {
        console.error("Error updating data in MongoDB:", error);
        if (error.response) {
            console.error("Response status:", error.response.status);
            console.error("Response data:", error.response.data);
        }
        throw error;
    }
};


 

/**
 * Fetches heatmap data keys via the proxy API
 * @param {string} collectionName - The name of the MongoDB collection
 * @param {string} graphName - The graph name identifier
 * @param {string} availableKeys - The available keys parameter
 * @param {string|number} year - The year parameter
 * @param {string|number} month - The month parameter
 * @returns {Promise} - Returns the response from the API
 */
const getHeatmapDataKeys = async (collectionName, graphName, availableKeys, year, month) => {
    try {
        const baseUrl = "https://server.causalfunnels.com/api/heatmap-data-keys/available-keys";
        
        const response = await axios.get(baseUrl, {
            params: {
                collectionName: collectionName,
                graphName: graphName,
                availableKeys: availableKeys,
                year: year,
                month: month
            },
            headers: {
                'Content-Type': 'application/json'
            }
        });
        
        return response.data;
    } catch (error) {
        console.error("Error fetching heatmap data keys:", error);
        throw error;
    }
};

/**
 * Fetches heatmap data via the proxy API
 * @param {string} collectionName - The name of the MongoDB collection
 * @param {string} graphName - The graph name identifier
 * @param {string|number} year - The year parameter
 * @param {string|number} month - The month parameter
 * @param {string} week - The week parameter
 * @returns {Promise} - Returns the response from the API
 */
const getHeatmapData = async (collectionName, graphName, year, month, week) => {
    try {
        const baseUrl = "https://server.causalfunnels.com/api/heatmap-data";
        
        const response = await axios.get(baseUrl, {
            params: {
                collectionName: collectionName,
                graphName: graphName,
                year: year,
                month: month,
                week: week
            },
            headers: {
                'Content-Type': 'application/json'
            }
        });
        
        return response.data;
    } catch (error) {
        console.error("Error fetching heatmap data:", error);
        throw error;
    }
};

/**
 * Deletes data from MongoDB via the proxy API
 * @param {string} collectionName - The name of the MongoDB collection
 * @param {string} graphName - The graph name identifier
 * @param {Array<string>} path - Array representing the nested path to delete (e.g., ["airkauaihelicopters", "chatbot_data", "20"])
 * @returns {Promise} - Returns the response from the API
 */
const deleteDataInMongoDb = async (collectionName, graphName, path) => {
    try {
        const baseUrl = `${process.env.REACT_SERVER_URL}/api/delete-dashboard-data`;
        
        const response = await axios({
            method: 'delete',
            url: baseUrl,
            data: {
                collectionName: collectionName,
                graphName: graphName,
                path: path
            },
            headers: {
                'Content-Type': 'application/json'
            }
        });
        
        return response.data;
    } catch (error) {
        console.error("Error deleting data from MongoDB:", error);
        if (error.response) {
            console.error("Response status:", error.response.status);
            console.error("Response data:", error.response.data);
            
            // Check if it's a 404 with a specific message pattern (API might be working but returning wrong status)
            if (error.response.status === 404 && error.response.data && 
                error.response.data.message && 
                error.response.data.message.includes('Invalid array index')) {
                console.log("Note: This might be an API issue - deletion may have succeeded despite the 404 error");
            }
        }
        throw error;
    }
};

export {
    getDataFromMongoDb,
    updateDataInMongoDb,
    deleteDataInMongoDb,
    getDataInMongoDbByPath,
    getKeysInMongoDbByPath,
    getHeatmapDataKeys,
    getHeatmapData
};


export default { 
    getDataFromMongoDb, 
    updateDataInMongoDb, 
    deleteDataInMongoDb, 
    getDataInMongoDbByPath, 
    getKeysInMongoDbByPath,
    getHeatmapDataKeys,
    getHeatmapData
};